import React from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';

import { useTranslation } from 'react-i18next';

export interface BreadcrumbItem {
  name: string;
  icon?: ({ className }: { className?: string }) => React.ReactElement;
  to?: string;
}

interface CustomBreadcrumbsProps {
  items: BreadcrumbItem[];
}

/**
 * Custom breadcrumbs component that works with TanStack Router
 * Replaces the design-system Breadcrumbs component which uses react-router
 */
export const CustomBreadcrumbs: React.FC<CustomBreadcrumbsProps> = ({
  items,
}) => {
  const { t } = useTranslation('pleri/chat');
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <nav
      className="flex items-center gap-1.5 text-sm"
      aria-label={t('breadcrumb')}
    >
      <ol className="flex items-center gap-1.5">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const Icon = item.icon;

          const content = (
            <span className="flex items-center gap-1.5">
              {Icon && (
                <Icon className="h-4 w-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
              )}
              <span
                className={`truncate ${
                  isLast
                    ? 'font-medium text-gray-900 dark:text-gray-100'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                }`}
              >
                {item.name}
              </span>
            </span>
          );

          return (
            <li key={index} className="flex items-center gap-1.5">
              {item.to && !isLast ? (
                <Link
                  to={item.to}
                  className="flex items-center gap-1.5 transition-colors hover:text-gray-900 dark:hover:text-gray-100"
                >
                  {content}
                </Link>
              ) : (
                <span className="flex items-center">{content}</span>
              )}
              {!isLast && (
                <ChevronRight
                  className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500"
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
